/**
 * InviteAccept (Sprint 9 B1) — public page `/invite/:token`.
 *
 * Flow:
 *   1. Mount → GET /api/auth/invite/status/<token>/ preflight
 *   2. Render theo status:
 *      - loading: Spin "Đang kiểm tra"
 *      - valid: Form set password + first/last name
 *      - expired/accepted/invalid: Result với hint
 *   3. Submit form → POST /api/auth/invite/accept/ → JWT auto-login
 *   4. Redirect by role (chốt 2026-05-21):
 *      student → /my-courses, instructor → /admin/courses,
 *      tenant_admin & superadmin → /admin/analytics
 *   5. Toast welcome top-right 3s — UX polish first-time experience
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Result,
  Space,
  Spin,
  Typography,
} from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
  acceptInvite,
  getInviteStatus,
  type InviteStatusResponse,
  type User,
} from "@/api/auth";
import { useAuthStore } from "@/stores/auth";

const { Title, Text, Paragraph } = Typography;

type ViewStatus = "loading" | "valid" | "expired" | "accepted" | "invalid";

const REDIRECT_BY_ROLE: Record<User["role"], string> = {
  student: "/my-courses",
  instructor: "/admin/courses",
  tenant_admin: "/admin/analytics",
  superadmin: "/admin/analytics",
};

const ROLE_LABEL: Record<User["role"], string> = {
  student: "Học viên",
  instructor: "Giảng viên",
  tenant_admin: "Quản trị tenant",
  superadmin: "Quản trị hệ thống",
};

export function InviteAccept() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewStatus>("loading");
  const [meta, setMeta] = useState<InviteStatusResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInviteStatus(token)
      .then((data) => {
        if (cancelled) return;
        setMeta(data);
        setView("valid");
      })
      .catch((err: { response?: { status?: number; data?: { detail?: string } } }) => {
        if (cancelled) return;
        const httpStatus = err.response?.status;
        const detail = err.response?.data?.detail ?? "";
        if (httpStatus === 410) {
          // Phân biệt expired vs accepted theo message — BE trả detail rõ ràng
          if (/sử dụng/i.test(detail)) {
            setView("accepted");
          } else {
            setView("expired");
          }
        } else {
          setView("invalid");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (values: {
    password: string;
    first_name: string;
    last_name: string;
  }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await acceptInvite({
        token,
        password: values.password,
        first_name: values.first_name,
        last_name: values.last_name,
      });
      // Set tokens + user vào auth store (mirror login flow)
      useAuthStore.setState({
        user: res.user,
        accessToken: res.access,
        refreshToken: res.refresh,
        isAuthenticated: true,
      });
      message.success({
        content: "Tài khoản đã kích hoạt thành công. Chào mừng bạn đến với Savina!",
        duration: 3,
      });
      const target = REDIRECT_BY_ROLE[res.user.role] ?? "/";
      navigate(target, { replace: true });
    } catch (err: unknown) {
      const resp = (
        err as {
          response?: {
            status?: number;
            data?: { detail?: string; password?: string[] };
          };
        }
      )?.response;
      if (resp?.status === 400 && resp?.data?.password) {
        const msgs = Array.isArray(resp.data.password)
          ? resp.data.password.join(" ")
          : String(resp.data.password);
        setSubmitError(`Mật khẩu yếu: ${msgs}`);
      } else if (resp?.status === 400) {
        setSubmitError(resp?.data?.detail ?? "Không thể kích hoạt tài khoản.");
      } else if (resp?.status === 404) {
        setSubmitError("Lời mời không hợp lệ. Vui lòng liên hệ quản trị viên.");
      } else if (resp?.status === 410) {
        setSubmitError("Lời mời đã hết hạn hoặc đã được sử dụng.");
      } else {
        setSubmitError("Có lỗi xảy ra. Vui lòng thử lại.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render states ────────────────────────────────────────────────────

  if (view === "loading") {
    return (
      <CenterCard>
        <Space direction="vertical" align="center" style={{ width: "100%" }}>
          <Spin size="large" />
          <Text>Đang kiểm tra lời mời...</Text>
        </Space>
      </CenterCard>
    );
  }

  if (view === "expired") {
    return (
      <CenterCard>
        <Result
          status="warning"
          title="Lời mời đã hết hạn"
          subTitle={
            <Paragraph>
              Lời mời này không còn hợp lệ. Vui lòng{" "}
              <strong>liên hệ quản trị viên</strong> để được gửi lại lời mời mới.
            </Paragraph>
          }
        />
      </CenterCard>
    );
  }

  if (view === "accepted") {
    return (
      <CenterCard>
        <Result
          status="info"
          title="Lời mời đã được sử dụng"
          subTitle={
            <Paragraph>
              Tài khoản đã được kích hoạt trước đó. Vui lòng đăng nhập với mật khẩu
              của bạn.
            </Paragraph>
          }
          extra={
            <Button type="primary" onClick={() => navigate("/login")}>
              Đến trang đăng nhập
            </Button>
          }
        />
      </CenterCard>
    );
  }

  if (view === "invalid") {
    return (
      <CenterCard>
        <Result
          status="error"
          title="Lời mời không hợp lệ"
          subTitle={
            <Paragraph>
              Liên kết bạn truy cập không hợp lệ. Vui lòng{" "}
              <strong>liên hệ quản trị viên</strong> để kiểm tra lại lời mời.
            </Paragraph>
          }
        />
      </CenterCard>
    );
  }

  // view === "valid" — render form
  return (
    <CenterCard>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ textAlign: "center" }}>
          <Title level={2} style={{ margin: 0 }}>
            Hoàn tất đăng ký
          </Title>
          {meta && (
            <Text type="secondary">
              Bạn được mời vào <strong>{meta.tenant_name}</strong> với vai trò{" "}
              <strong>{ROLE_LABEL[meta.role] ?? meta.role}</strong>.<br />
              Email: <strong>{meta.email}</strong>
            </Text>
          )}
        </div>

        {submitError && <Alert type="error" message={submitError} showIcon closable />}

        <Form
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          autoComplete="off"
        >
          <Space style={{ width: "100%" }} size="small">
            <Form.Item
              name="last_name"
              label="Họ"
              style={{ width: "100%" }}
              rules={[{ required: true, message: "Vui lòng nhập họ." }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Nguyễn" size="large" />
            </Form.Item>
            <Form.Item
              name="first_name"
              label="Tên"
              style={{ width: "100%" }}
              rules={[{ required: true, message: "Vui lòng nhập tên." }]}
            >
              <Input placeholder="An" size="large" />
            </Form.Item>
          </Space>

          <Form.Item
            name="password"
            label="Mật khẩu"
            rules={[
              { required: true, message: "Vui lòng nhập mật khẩu." },
              { min: 10, message: "Mật khẩu tối thiểu 10 ký tự." },
            ]}
            extra="Tối thiểu 10 ký tự, không quá phổ biến, không toàn số."
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••••"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              size="large"
            >
              Kích hoạt tài khoản
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </CenterCard>
  );
}

function CenterCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
      }}
    >
      <Card style={{ width: 480, padding: "16px 8px" }}>{children}</Card>
    </div>
  );
}

export default InviteAccept;
